"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import {
  EcommerceTermsProvider,
  useEcommerceTerms,
} from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";

function HeaderContent() {
  const { links, loading: breadcrumbsLoading } = useShopBreadcrumbs();
  const { terms } = useEcommerceTerms();

  if (breadcrumbsLoading) {
    return (
      <div className="flex flex-col items-center justify-center max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 animate-pulse">
        <div className="h-6 w-64 bg-muted rounded-md mb-6"></div>
        <div className="h-12 w-96 bg-muted rounded-md mb-6"></div>
      </div>
    );
  }

  
  const currentLink = links.length > 0 ? links[links.length - 1] : { title: "Shop", href: "#" };
  const breadcrumbTrail = links.length > 0 ? links : [{ title: "Home", href: "/" }, { title: "Shop", href: "/shop" }];

  
  const currentTerm = terms.find((t) => t.name === currentLink.title);

  return (
    <div className="flex flex-col items-center text-center max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <Breadcrumb className="mb-6">
        <BreadcrumbList className="justify-center">
          {breadcrumbTrail.map((link, i) => {
            const isLast = i === breadcrumbTrail.length - 1;
            return (
              <BreadcrumbItem key={link.href || i} data-index={i}>
                {isLast ? (
                  <BreadcrumbPage className="text-primary font-medium text-base">
                    {link.title}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link
                      to={link.href}
                      className="text-muted-foreground hover:text-primary transition-colors text-base"
                    >
                      {link.title}
                    </Link>
                  </BreadcrumbLink>
                )}
                {!isLast && <BreadcrumbSeparator className="ml-1.5 sm:ml-2.5" />}
              </BreadcrumbItem>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>

      <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-primary mb-6 tracking-tight">
        {currentLink.title}
      </h1>

      {currentTerm?.description && (
        <p className="text-lg md:text-xl text-muted-foreground font-sans max-w-2xl mx-auto leading-relaxed">
          {currentTerm.description}
        </p>
      )}
    </div>
  );
}

export default function PageTitleAndBreadcrumbs() {
  
  const isClient = typeof window !== "undefined";
  const current_wp_term_query_params =
    isClient && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPTermQueryParams()
      : {};
  const wp_term_query = new WP_Term_Query(current_wp_term_query_params);

  return (
    <section
      id="page-title-and-breadcrumbs"
      className="bg-background py-16 md:py-24 border-b border-border/40"
    >
      <ShopBreadcrumbsProvider>
        <EcommerceTermsProvider wp_term_query={wp_term_query}>
          <HeaderContent />
        </EcommerceTermsProvider>
      </ShopBreadcrumbsProvider>
    </section>
  );
}